<template>
  <div>
    <div>
      <div class="col-12 mb-3" v-if="$auth.can({audit_log: 'read'}, 'permissions')">
        <form class="d-flex justify-content-end align-items-center">
          <el-input
            type="search"
            class="col-lg-4 col-md-6 col-sm-10 select-primary"
            :placeholder="$t('auditLogs.filterForm.provideID')"
            v-model="filterForm.id"
            aria-controls="datatables"
          >
          </el-input>
          <el-select
            class="col-lg-4 col-md-6 col-sm-10 select-primary"
            multiple
            collapse-tags
            v-model="filterForm.resources"
            :loading="auditLogResourcesLoading"
            :placeholder="$t('auditLogs.filterForm.selectResourcesPlaceholder')"
            :no-match-text="$t('auditLogs.filterForm.selectResourcesNoMatchTextNotification')"
            :no-data-text="$t('auditLogs.filterForm.selectResourcesNoDataTextNotification')"
            :filterable="true"
            :clearable="true"
          >
            <el-option
              v-for="item in auditLogResources"
              :key="item"
              :label="item"
              :value="item">
            </el-option>
          </el-select>

          <el-select
            class="select-primary"
            v-model="filterForm.action_result"
            :placeholder="$t('auditLogs.filterForm.selectResultActionPlaceholder')"
            :clearable="true"
          >
            <el-option
              v-for="item in auditLogActionResults"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
          <base-button :loading="loading" type="default" @click="applyFilter" native-type="submit" class="ml-1">
            <i class="tim-icons icon-light-3 pr-2"/> {{ $t('auditLogs.filterForm.filerBtnLabel') }}
          </base-button>
        </form>
      </div>
      <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap mb-3">
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          placeholder="Per page">
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item">
          </el-option>
        </el-select>

        <base-input>
          <el-input
            type="search"
            class="search-input"
            prefix-icon="el-icon-search"
            :placeholder="$t('generic.searchInTablePlaceholder')"
            v-model="searchQuery"
            aria-controls="datatables">
          </el-input>
        </base-input>
      </div>

      <el-table v-loading="loading" :data="queriedData" :row-class-name="tableRowClassName"
                :empty-text="$t('generic.noData')">
        <el-table-column :label="$t('generic.created')" width="200">
          <div slot-scope="{ row }">
            {{ row.created_at | time }}
          </div>
        </el-table-column>
        <el-table-column :label="$t('auditLogs.data.audit_group')" width="100">
          <div slot-scope="{ row }">
            {{ row.audit_group }}
          </div>
        </el-table-column>
        <el-table-column :label="$t('auditLogs.data.account_id')">
          <div slot-scope="{ row }">
            <router-link v-if="row.account_id" :to="initiatorLink(row)">{{ row.account_id }}</router-link>
          </div>
        </el-table-column>
        <el-table-column :label="$t('auditLogs.data.username')">
          <div slot-scope="{ row }">
            {{ row.username }}
          </div>
        </el-table-column>
        <el-table-column :label="$t('auditLogs.data.action')" width="100">
          <div slot-scope="{ row }">
            {{ row.action }}
          </div>
        </el-table-column>
        <el-table-column :label="$t('auditLogs.data.resource_kind')" width="150">
          <div slot-scope="{ row }">
            {{ row.resource_kind }}
          </div>
        </el-table-column>
        <el-table-column :label="$t('auditLogs.data.resource_owner_id')">
          <div slot-scope="{ row }">
            <router-link v-if="row.resource_owner_id!==''" :to="accountAuditLogsLink(row.resource_owner_id)">{{
                row.resource_owner_id
              }}
            </router-link>
          </div>
        </el-table-column>
        <el-table-column :label="$t('auditLogs.data.message')">
          <div slot-scope="{ row }">
            {{ row.message }}
          </div>
        </el-table-column>
        <el-table-column :label="$t('auditLogs.data.resource_id')">
          <div slot-scope="{ row }">
            {{ row.resource_id }}
          </div>
        </el-table-column>
        <el-table-column :label="$t('auditLogs.data.metadata')" min-width="180">
          <div slot-scope="{ row }">
            <meta-data-details :data="row.metadata" as-list/>
          </div>
        </el-table-column>
        <el-table-column
          v-if="$auth.can({audit_log: 'read'}, 'permissions')"
          align="right">
          <div slot-scope="props">
            <router-link
              :to="handleNavigate(props.row)">
              <i class="tim-icons icon-double-right"></i>
            </router-link>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div slot="footer" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
      <div class="">
        <p class="card-category">
          {{ $t('generic.tablePaginationSummary', {from: from + 1, to: to, total: total}) }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      >
      </base-pagination>
    </div>
  </div>
</template>

<script>
import {BaseAlert, BasePagination} from "@/components";
import MetaDataDetails from "src/components/MetaData/MetaDataDetails";
import {Option, Select, Table, TableColumn} from "element-ui";
import {mapActions, mapState} from "vuex";
import Fuse from "fuse.js";
import linkUtils from "@/util/link_utils";

export default {

  name: "AuditLogTable",
  components: {
    BasePagination,
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    MetaDataDetails
  },
  props: {
    accountAuditLogs: {
      type: Boolean,
      required: true,
      description: 'Set this property to true if table should display audit logs for single account.'
    },
  },
  methods: {
    ...mapActions(['LoadAccountAuditLogs', 'LoadAuditLogs', 'LoadAuditLogResources']),
    handleNavigate(row) {
      if (this.accountAuditLogs === true) {
        return {name: 'account-audit-log-details', params: {id: row.resource_owner_id, resource_id: row.id}}
      } else {
        return {name: 'auditLog', params: {id: row.id}}
      }
    },
    tableRowClassName({row, rowIndex}) {
      if (row["action_result"] === "failure") {
        return 'bg-warning';
      }
      return '';
    },
    applyFilter: function (event) {
      if (event) {
        event.preventDefault()
      }
      // const oldFilter = this.apiQuery.filter
      // const oldActionResult = this.apiQuery.action_result
      this.filterForm.id = this.filterForm.id.trim()
      this.apiQuery.id = this.filterForm.id
      this.apiQuery.filter = this.filterForm.resources.join()
      this.apiQuery.action_result = this.filterForm.action_result
      // if (oldFilter == this.apiQuery.filter && oldActionResult == this.apiQuery.action_result) {
      //   return
      // }
      if (this.accountAuditLogs) {
        this.LoadAccountAuditLogs({id: this.$route.params.id, query: this.apiQuery})
      } else {
        this.LoadAuditLogs(this.apiQuery)
      }
    },
    initiatorLink(row) {
      if (row.audit_group == "user") {
        return linkUtils.accountProfilePath(this.$router, {account_id: row.account_id});
      } else {
        return linkUtils.systemAccountPath(this.$router, {system_account_id: row.account_id});
      }
    },
    accountAuditLogsLink(accountID) {
      return linkUtils.accountAuditLogsPath(this.$router, {account_id: accountID});
    },
  },
  computed: {
    ...mapState({
      auditLogResourcesLoading: state => state.audit_log_resources.loading,
      auditLogResources: state => state.audit_log_resources.data,
      loading(state) {
        if (this.accountAuditLogs) {
          return state.account_audit_logs.loading
        }

        return state.audit_logs.loading
      }
    }),
    /***
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    queriedData() {
      let result = this.tableData;
      if (this.searchedData.length > 0) {
        result = this.searchedData;
      }
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.totalCount;
    },
    auditLogActionResults() {
      return [{
        value: 'failure',
        label: this.$t('auditLogs.filterForm.selectResultActionValueFailure')
      }, {
        value: 'OK',
        label: this.$t('auditLogs.filterForm.selectResultActionValueOK')
      }]
    },
  },
  data() {
    return {
      filterForm: {
        id: '',
        resources: [],
        action_result: ''
      },
      apiQuery: {
        id: '',
        filter: '',
        limit: 300,
        action_result: ''
      },
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      searchQuery: '',
      propsToSearch: ['action', 'created_at', 'resource_kind'],
      tableColumns: [

        {
          prop: 'message',
          label: 'auditLogs.data.message',
        },
        {
          prop: 'resource_id',
          label: 'auditLogs.data.resource_id',
        }
      ],
      tableData: [],
      totalCount: 0,
      searchedData: [],
      fuseSearch: null,
    };
  },
  mounted() {
    if (this.$auth.can({audit_log: 'read'}, 'permissions') === true && (this.auditLogResources === undefined || this.auditLogResources == 0)) {
      // Load resources if only they were not loaded.
      this.LoadAuditLogResources()
    }

    let watchTableData = '$store.state.audit_logs.data'
    if (this.accountAuditLogs) {
      this.LoadAccountAuditLogs({id: this.$route.params.id, query: this.apiQuery})
      watchTableData = '$store.state.account_audit_logs.data'
    } else {
      this.LoadAuditLogs(this.apiQuery)
    }

    this.$watch(watchTableData, function (auditLogs) {
      this.tableData = auditLogs;
      this.totalCount = 0;
      if (auditLogs) {
        this.totalCount = auditLogs.length;
      }
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: ['action', 'created_at', 'resource_kind'],
        threshold: 0.3
      });
    });
  },
  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData;
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery);
      }
      this.searchedData = result;
    },
  }
}
</script>

<style scoped>

</style>
